
import { Vue, Component } from 'vue-property-decorator';
import store from '@/store';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import FeedbackModel from '@/Pages/CancelSubscriptionPage/FeedbackModel';
import axios, { AxiosError } from 'axios';
import AppConfig from '@/AppConfig';
import * as Sentry from '@sentry/vue';
import Icon from '@/components/Icons/Icon.vue';
import EmptyState from '@/components/EmptyState.vue';
import QrCheckbox from '@/components/Input/QrCheckbox.vue';
import moment from 'moment';
import { trackCancelSubscription } from '@/Services/Snowplow/SnowplowService';
import { SnowplowCancellationReasonIdentifier } from '@/Services/Snowplow/models';

@Component({
  components: {
    Icon,
    EmptyState,
    QrCheckbox,
  },
})
export default class CancelSubscription extends Vue {
  // DATA
  form: FeedbackModel = new FeedbackModel();
  moment = moment;
  password = '';
  showPassword = false;
  questionFourSelected: any = [];
  answerFour: Array<any> = [];

  subscription = store.state.subscription.activeSubscription;
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();

  // Loading states
  isUnsubscribing = false;
  isUnsubscribingError = false;
  // DATA END

  // FORM VALIDATION
  valid = true;
  submitted = false;
  get messageRules(): any {
    return [
      (v: string) => {
        return !!v || this.$t('fields.account.billing.cancel_subs.page_field_required');
      },
    ];
  }
  get passwordRules(): any {
    return [
      (v: string) => {
        return !!v || this.$t('fields.account.billing.cancel_subs.page_field_required');
      },
    ];
  }
  get checkboxesInvalid(): boolean {
    return this.questionFourSelected.length !== this.answerFour.length;
  }

  get validateCancellationReasons(): boolean {
    if (!this.isCancellationReasonsEnabled) return true;
    return !!this.selectedCancellationOption;
  }

  // METHODS
  beforeRouteEnter(to: any, from: any, next: any): void {
    if (to.name === 'cancel_subscription' && from.path !== '/my-account/billing') next({ path: '/my-account/billing' });
    next();
  }

  async cancelSubscription(): Promise<void> {
    this.submitted = true;
    if ((this as any).$refs.form.validate() && !this.checkboxesInvalid && this.validateCancellationReasons) {
      return await axios
        .post(AppConfig.getAPIBaseUrl() + '/user/verify-password', { password: this.password })
        .then((response) => {
          if (response.data?.success) {
            this.isUnsubscribing = true;
            this.stripeSubscriptionService
              .cancel(this.subscription.id)
              .then(async () => {
                if (this.isCancellationReasonsEnabled) {
                  this.snowplowTrackCancelSubscription(true);
                  await this.cancelSubscriptionSuccess();
                } else {
                  await this.sendFeedback();
                }
              })
              .catch(() => {
                this.isUnsubscribingError = true;
              });
          } else {
            store.dispatch('layout/setSnackbar', {
              type: 'info',
              icon: 'info-system',
              text: this.$t('fields.account.billing.cancel_subs.page_pwd_invalid'),
              isSnackbar: true,
            });
          }
        });
    } else {
      if (this.isCancellationReasonsEnabled) {
        this.snowplowTrackCancelSubscription(false);
      }
    }
  }

  snowplowTrackCancelSubscription(success: boolean): void {
    const user_id = store.state?.auth?.user?.id;
    const account = store.state?.account?.account;
    let reason_text =
      this.cancellationReasons?.find((item) => item.value === this.selectedCancellationOption)?.label || null;
    if (this.selectedCancellationOption === 'other') {
      reason_text = this.cancellationOtherReason;
    }
    trackCancelSubscription({
      industry_id: account?.industry_id ?? null,
      user_id,
      reason_identifier: this.selectedCancellationOption,
      reason_text,
      frontend_validation_succeeded: success,
    });
  }

  async cancelSubscriptionSuccess(): Promise<void> {
    await store.dispatch('layout/setSnackbar', {
      type: 'info',
      icon: 'info-system',
      text: this.$t('fields.account.billing.cancel_subs.page_subs_ok'),
      isSnackbar: false,
    });
    await this.$router.push({ name: 'my_account_tab', params: { tab: 'billing' } });
  }

  async sendFeedback(): Promise<void> {
    await axios
      .post(AppConfig.getAPIBaseUrl() + '/feedback', this.form)
      .then(async () => {
        await this.cancelSubscriptionSuccess();
      })
      .catch((error: AxiosError) => {
        Sentry.captureException(error);
        this.isUnsubscribingError = true;
      })
      .finally(() => {
        this.isUnsubscribing = false;
      });
  }

  onKeepSubscription(): void {
    this.$router.push({ name: 'my_account_tab', params: { tab: 'billing' } });
  }
  // METHODS END

  created(): void {
    // Translation
    this.answerFour.push(
      this.$t('fields.account.billing.cancel_subs.page_check_one'),
      this.$t('fields.account.billing.cancel_subs.page_check_two'),
      this.$t('fields.account.billing.cancel_subs.page_check_three'),
      this.$t('fields.account.billing.cancel_subs.page_check_four')
    );

    // Translate reasons
    this.cancellationReasons.push({
      id: 1,
      label: this.$t('fields.account.billing.cancel_subs.page_option_one') as string,
      value: 'missing_features',
    });
    this.cancellationReasons.push({
      id: 2,
      label: this.$t('fields.account.billing.cancel_subs.page_option_two') as string,
      value: 'technical_problems',
    });
    this.cancellationReasons.push({
      id: 3,
      label: this.$t('fields.account.billing.cancel_subs.page_option_three') as string,
      value: 'bad_outcome',
    });
    this.cancellationReasons.push({
      id: 4,
      label: this.$t('fields.account.billing.cancel_subs.page_option_four') as string,
      value: 'not_using_enough',
    });
    this.cancellationReasons.push({
      id: 5,
      label: this.$t('fields.account.billing.cancel_subs.page_option_five') as string,
      value: 'project_finished',
    });
    this.cancellationReasons.push({
      id: 6,
      label: this.$t('fields.account.billing.cancel_subs.page_option_six') as string,
      value: 'too_expensive',
    });
    this.cancellationReasons.push({
      id: 7,
      label: this.$t('fields.account.billing.cancel_subs.page_option_seven') as string,
      value: 'unsure_how_to_use',
    });
    this.cancellationReasons.push({
      id: 8,
      label: this.$t('fields.account.billing.cancel_subs.page_option_eight') as string,
      value: 'other',
    });
  }

  // Cancel Subscription reasons
  selectedCancellationOption: SnowplowCancellationReasonIdentifier = '' as SnowplowCancellationReasonIdentifier;
  cancellationOtherReason = '';
  cancellationReasons: {
    id: number;
    label: string;
    value: string;
  }[] = [];

  get isCancellationReasonsEnabled(): boolean {
    return AppConfig.getCancellationReasonsEnabled() === 'true';
  }
}
